// src/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../components/shared/Modal';
import SessionExpired from '../components/auth/SessionExpired';
import { setGlobalErrorHandler } from '../utils/authInterceptor';
import { setApiErrorHandler, authenticatedFetch } from '../utils/apiClient';
import { setGlobalErrorHandler as setGlobalWindowErrorHandler } from '../utils/globalErrorHandler';
import logger from '../utils/logger';
import { identify } from '../utils/analytics';
export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(localStorage.getItem('token'));
  const [userData, setUserData] = useState(null);
  const [usageRemaining, setUsageRemaining] = useState(null);
  const [sessionModal, setSessionModal] = useState({ isOpen: false, message: '' });
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const navigate = useNavigate();

  const fetchAnalyticsIdentity = async () => {
    try {
      const resp = await authenticatedFetch(`${process.env.REACT_APP_API_BASE_URL}/api/analytics/identity`);
      if (!resp.ok) return;
      const data = await resp.json();
      const distinctId = data?.distinct_id;
      const traits = data?.traits || {};
      // Attach consent hint if present in localStorage
      try { traits.marketing_consent = localStorage.getItem('marketing_consent') === 'true'; } catch (_) {}
      identify(distinctId, traits);
    } catch (e) {
      // Best-effort; ignore
    }
  };

  const storeToken = (token) => {
    if (token) {
      localStorage.setItem('token', token);
      setAuthToken(token);
      setIsAuthenticated(true);
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser(payload.sub);
        // After setting token, fetch analytics identity
        fetchAnalyticsIdentity();
      } catch (e) {
        logger.error('Error parsing token:', e);
      }
    }
  };

  const storeUserData = (data) => {
    logger.info('Storing user data:', data);
    setUserData(data);
    localStorage.setItem('userData', JSON.stringify(data));
  };

  const refreshUserData = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await authenticatedFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/user/profile`
      );

      const data = await response.json();
      storeUserData(data);
      // Best-effort refresh of analytics ID/traits after profile refresh
      fetchAnalyticsIdentity();
      return data;
    } catch (error) {
      logger.error('Error refreshing user data:', error);
      handleApiError(error);
    }
  };

  const clearToken = (isManualLogout = false) => {
    logger.info('Clearing token and state');
    localStorage.removeItem('token');
    localStorage.removeItem('lastTokenTime');
    localStorage.removeItem('cachedAnswer');
    localStorage.removeItem('cachedImages');
    localStorage.removeItem('cachedQuestion');
    localStorage.removeItem('cachedCategory');
    localStorage.removeItem('userData');
    setAuthToken(null);
    setIsAuthenticated(false);
    setUserData(null);
    setUsageRemaining(null);
    
    // Only show the session expired modal if it's not a manual logout
    if (!isManualLogout) {
      setShowSessionExpired(true);
    }
  };

  const handleApiError = (error) => {
    logger.info('handleApiError called with:', { 
      status: error.response?.status, 
      message: error.message,
      type: error.name 
    });

    // Check for 401 or 403 status codes which indicate authentication issues
    if (error.response?.status === 401 || error.response?.status === 403) {
      logger.info('Authentication error detected:', error.response.status);
      clearToken();
      return; // Stop error propagation for auth errors
    }

    // Try to parse the error message if it's a string
    let errorData;
    try {
      errorData = typeof error.message === 'string' ? 
        JSON.parse(error.message.replace('Server error: ', '')) : 
        error.response?.data;
    } catch (e) {
      errorData = null;
    }

    // Handle specific error codes and messages
    if (errorData?.code === 'SESSION_EXPIRED' || 
        errorData?.message?.includes('expired') || 
        errorData?.message?.includes('Expired') ||
        error.message?.includes('Token has expired')) {
      logger.info('Session expired error detected');
      clearToken();
      return; // Stop error propagation for this specific error
    }

    // Handle JWT specific errors
    if (error.message?.includes('jwt') || 
        error.message?.includes('JWT') || 
        error.message?.includes('Token') ||
        error.message?.includes('Authentication error')) {
      logger.info('Token-related error detected');
      clearToken();
      return;
    }

    // If it's not an auth error, re-throw it for other handlers
    throw error;
  };

  // Function to validate token
  const validateToken = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 < Date.now()) {
          logger.info('Token expired during validation');
          localStorage.clear();
          setIsAuthenticated(false);
          setUser(null);
          return false;
        }
        return true;
      } catch (e) {
        logger.error('Error validating token:', e);
        localStorage.clear();
        setIsAuthenticated(false);
        setUser(null);
        return false;
      }
    }
    return false;
  };

  const handleModalClose = () => {
    setSessionModal({ isOpen: false, message: '' });
    navigate('/login');
  };

  // Set up global error handlers
  useEffect(() => {
    setGlobalErrorHandler(handleApiError);
    setApiErrorHandler(handleApiError);
    setGlobalWindowErrorHandler(handleApiError);
  }, []);

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const storedUserData = localStorage.getItem('userData');
    if (token && validateToken()) {
      setIsAuthenticated(true);
      // Set user from token if needed
      fetchAnalyticsIdentity();
    }
    if (storedUserData) {
      setUserData(JSON.parse(storedUserData));
    }
    setLoading(false);
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      setIsAuthenticated, 
      user, 
      setUser,
      authToken,
      storeToken,
      clearToken,
      userData,
      storeUserData,
      refreshUserData,
      usageRemaining,
      setUsageRemaining,
      handleApiError
    }}>
      <Modal 
        isOpen={sessionModal.isOpen}
        message={sessionModal.message}
        onClose={handleModalClose}
      />
      {showSessionExpired && (
        <SessionExpired onClose={() => setShowSessionExpired(false)} />
      )}
      {children}
    </AuthContext.Provider>
  );
}
